/* globals document */
import { React, PT, WithData, fetchApi, withRouter } from '@gasbuddy/react';
import moment from 'moment';
import { Dimmer, Loader, Table, Segment, Button, Modal, Icon, Form, Grid, Checkbox, TextArea } from 'semantic-ui-react';
import { toFunction } from 'async-rule-evaluator';
import { Auth, Nav } from '../../components';
import { Role } from '../../roles';
import { AddFeatureVariant } from './AddFeatureVariant';

function remainingDisplayValue(v) {
  if (v || v === 0) {
    return v;
  }
  return '-';
}

class BaseFeature extends React.Component {
  state = {
    column: 'name',
    direction: 'ascending',
  }

  setFeatureTime = (startOrStop) => {
    this.setState({ changing: true, error: null }, async () => {
      const { fetch: fetchFn, featureId } = this.props;
      const { status, body, error } = await fetchApi({
        url: `/system/features/${startOrStop}/${featureId}`,
        catchErrors: true,
      });
      if (status !== 200) {
        this.setState({ changing: false, error: error ? error.toString() : body });
      } else {
        this.setState({ changing: false });
        fetchFn();
      }
    });
  }

  handleSort = clickedColumn => () => {
    const { column, direction } = this.state;

    if (column !== clickedColumn) {
      this.setState({
        column: clickedColumn,
        direction: 'ascending',
      });
    } else {
      this.setState({
        direction: direction === 'ascending' ? 'descending' : 'ascending',
      });
    }
  }

  saveChanges = () => {
    this.setState({ changing: true, error: null }, async () => {
      const { fetch: fetchFn, featureId } = this.props;
      const { analytics, record, cache, ignorePoints } = this.state;
      const { data: { feature } } = this.props;
      const {
        record_to_analytics: rta,
        record_assignments: rasgn,
        ignore_points: ip,
      } = feature;
      const { status, body, error } = await fetchApi({
        url: `/system/features/save/${featureId}`,
        body: {
          record_to_analytics: ('analytics' in this.state ? analytics : rta),
          record_assignments: ('record' in this.state ? record : rasgn),
          cache: ('cache' in this.state ? cache : undefined),
          ignore_points: ('ignorePoints' in this.state ? ignorePoints : ip),
        },
        catchErrors: true,
      });
      if (status !== 200) {
        this.setState({ changing: false, error: error ? error.toString() : body });
      } else {
        this.setState({
          changing: false,
        });
        fetchFn();
      }
    });
  }

  saveVersions = (e) => {
    e.preventDefault();
    this.setState({ changing: true, error: null }, async () => {
      const { fetch: fetchFn, featureId, data } = this.props;
      const { iosSemver, androidSemver, otherSemver } = this.state;
      const { status, body, error } = await fetchApi({
        url: `/system/features/save-semver/${featureId}`,
        body: {
          ios_semver: iosSemver === undefined ? data.feature.ios_semver : iosSemver,
          android_semver: androidSemver === undefined ? data.feature.android_semver : androidSemver,
          other_semver: otherSemver === undefined ? data.feature.other_semver : otherSemver,
        },
        catchErrors: true,
      });
      if (status !== 200) {
        this.setState({ changing: false, error: error ? error.toString() : body });
      } else {
        this.setState({
          changing: false,
        });
        fetchFn();
      }
    });
  }

  saveTag = () => {
    this.setState({ changing: true, error: null }, async () => {
      const { tag } = this.state;
      const { fetch: fetchFn, featureId } = this.props;
      const { status, body, error } = await fetchApi({
        url: `/system/features/add-tag/${featureId}`,
        body: { tag },
        catchErrors: true,
      });
      if (status !== 200) {
        this.setState({ changing: false, error: error ? error.toString() : body });
      } else {
        this.setState({
          changing: false,
          modal: null,
        });
        fetchFn();
      }
    });
  }

  saveEligibility = async (e) => {
    e.preventDefault();
    const { eligibility } = this.state;
    try {
      if (eligibility.trim()) {
        toFunction(eligibility);
      }
    } catch (error) {
      this.setState({ error: `The filter expression is invalid: ${error.message}` });
      return;
    }
    this.setState({ changing: true, error: null }, async () => {
      const { fetch: fetchFn, featureId } = this.props;
      const { status, body, error } = await fetchApi({
        url: `/system/features/eligibility/${featureId}`,
        body: { eligibility },
        catchErrors: true,
      });
      if (status !== 200) {
        this.setState({ changing: false, error: error ? error.toString() : body });
      } else {
        this.setState({
          changing: false,
          modal: null,
        });
        fetchFn();
      }
    });
  }

  render() {
    const {
      error, column, direction, analytics,
      record, changing, tag, modal, ignorePoints, cache,
      iosSemver, androidSemver, otherSemver, eligibility,
    } = this.state;
    const { data, loading, fetch: fetchFn, featureId, onSelected } = this.props;
    const { feature = {}, tags = [] } = data || {};

    const hasChanges = ('record' in this.state && record !== feature.record_assignments)
      || ('analytics' in this.state && analytics !== feature.record_to_analytics)
      || ('cache' in this.state);

    const dir = direction === 'ascending' ? 1 : -1;
    const variants = data ? data.variants.slice().sort((a, b) => {
      switch (column) {
        case 'name':
          return (a.name.localeCompare(b.name)) * dir;
        case 'remaining_assignments':
          return ((a.remaining_assignments - b.remaining_assignments) * dir)
            || (a.name.localeCompare(b.name));
        case 'points':
          return ((a.points - b.points) * dir)
            || (a.name.localeCompare(b.name));
        case 'desirability':
          return ((a.desirability - b.desirability) * dir)
            || (a.name.localeCompare(b.name));
        default:
          return 0;
      }
    }) : [];

    return (
      <Auth forRoles={[Role.Superuser, Role.SystemFeaturesView, Role.SystemFeaturesModify]}>
        <Nav>
          <div>
            <Modal
              open={error}
            >
              <Modal.Content>
                <p>An error occurred:</p>
                <pre>{JSON.stringify(error, null, '\t')}</pre>
              </Modal.Content>
              <Modal.Actions>
                <Button onClick={() => this.setState({ error: null })}>OK</Button>
              </Modal.Actions>
            </Modal>
            <Grid columns="equal">
              <Grid.Row>
                <Grid.Column>
                  <h1>Feature Details</h1>
                  <b>Name:</b>
                  {' '}
                  {feature.name}
&nbsp;(
                  {feature.internal_name}
)
                  <br />
                  <b>Tags:</b>
                  {' '}
                  {tags.map(x => x.tag).join(', ')}
                  <br />
                  <b>
                    <i>Ver</i>
                    {' '}
Value for Caching
                  </b>
:
                  {feature.return_if_before || 'not set'}
                  <br />
                  <b>Created:</b>
                  {' '}
                  {moment(feature.created_at).format()}
                  <br />
                  <b>
                    {feature.end_time ? 'Duration' : 'Start'}
:
                  </b>
                  {' '}
                  {feature.start_time ? moment(feature.start_time).format() : '[not started]'}
                  {feature.end_time ? ` - ${moment(feature.end_time).format()}` : ''}
                  <br />
                  <div style={{ marginTop: 10 }}>
                    <Checkbox
                      label="Cache Values On Clients and Don't Send This Feature Unless It Has Changed"
                      checked={!!('cache' in this.state ? cache : feature.return_if_before)}
                      onChange={(e, { checked }) => this.setState({ cache: checked })}
                    />
                    {(!('cache' in this.state) && feature.return_if_before)
                      ? <Button size="mini" style={{ marginLeft: 15 }} onClick={() => this.setState({ cache: true })}>Increment Ver Value</Button>
                      : null
                    }
                  </div>
                  <div style={{ marginTop: 10 }}>
                    <Checkbox
                      label="Record Assignments"
                      checked={!!('record' in this.state ? record : feature.record_assignments)}
                      onChange={(e, { checked }) => this.setState({ record: checked })}
                    />
                  </div>
                  <div style={{ marginTop: 10 }}>
                    <Checkbox
                      label="Save to Analytics"
                      checked={!!('analytics' in this.state ? analytics : feature.record_to_analytics)}
                      onChange={(e, { checked }) => this.setState({ analytics: checked })}
                    />
                  </div>
                  <div style={{ marginTop: 10 }}>
                    <Checkbox
                      label="Ignore Points"
                      checked={!!('ignorePoints' in this.state ? ignorePoints : feature.ignore_points)}
                      onChange={(e, { checked }) => this.setState({ ignorePoints: checked })}
                    />
                  </div>
                  {hasChanges
                    && (
                    <Button
                      loading={changing}
                      style={{ marginTop: 10 }}
                      onClick={this.saveChanges}
                    >
Save Changes
                    </Button>
                    )
                  }
                </Grid.Column>
                <Auth forRoles={[Role.Superuser, Role.SystemFeaturesModify]} hideUnauthorized>
                  <Grid.Column width={4} verticalAlign="bottom">
                    {!feature.start_time
                      && (
                      <Button
                        fluid
                        loading={changing}
                        color="green"
                        onClick={() => this.setFeatureTime('start')}
                      >
Start Feature Test
                      </Button>
                      )
                    }
                    <br />
                    <Modal
                      open={modal === 'addTag'}
                      trigger={(
                        <Button fluid onClick={() => this.setState({ modal: 'addTag' })}>
                          Add Feature To Tag
                        </Button>
)}
                    >
                      <Modal.Header>Add Feature To Tag</Modal.Header>
                      <Modal.Content>
                        <p>
A tag is a grouping of features that allows requesting apps
                            to get all assignments at once. For example, an app may request
                            gb.mobile.ios. It will get all features assigned to gb, gb.mobile
                            and gb.mobile.ios.
                        </p>
                        <Form loading={changing}>
                          <Form.Input
                            label="Tag"
                            placeholder="gb.mobile"
                            value={tag}
                            onChange={(e, { value }) => this.setState({ tag: value })}
                          />
                        </Form>
                      </Modal.Content>
                      <Modal.Actions>
                        <Button loading={changing} basic color="grey" onClick={() => this.setState({ modal: null })}>
                          <Icon name="minus" />
                          {' '}
Cancel
                        </Button>
                        <Button loading={changing} basic color="red" onClick={this.saveTag}>
                          <Icon name="plus" />
                          {' '}
Add Feature To Tag
                        </Button>
                      </Modal.Actions>
                    </Modal>
                    <br />
                    {!feature.end_time && !!feature.start_time
                      && (
                      <Button
                        fluid
                        loading={changing}
                        negative
                        onClick={() => this.setFeatureTime('stop')}
                      >
End Feature Test
                      </Button>
                      )
                    }
                  </Grid.Column>
                </Auth>
              </Grid.Row>
            </Grid>
            <Dimmer.Dimmable as={Segment} dimmed={loading}>
              <Dimmer active={loading}>
                <Loader />
              </Dimmer>
              <Table sortable striped selectable>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell textAlign="center" sorted={column === 'is_control_variant' && direction} onClick={this.handleSort('is_control_variant')}>Control</Table.HeaderCell>
                    <Table.HeaderCell sorted={column === 'name' && direction} onClick={this.handleSort('name')}>Name</Table.HeaderCell>
                    {!feature.ignore_points
                      && <Table.HeaderCell textAlign="center" sorted={column === 'points' && direction} onClick={this.handleSort('points')}>Points</Table.HeaderCell>
                    }
                    <Table.HeaderCell textAlign="center" sorted={column === 'remaining_assignments' && direction} onClick={this.handleSort('remaining_assignments')}>Remaining</Table.HeaderCell>
                    <Table.HeaderCell textAlign="center" sorted={column === 'desirability' && direction} onClick={this.handleSort('desirability')}>Desirability</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {variants.map(d => (
                    <Table.Row
                      key={d.feature_variant_id}
                      onClick={() => onSelected(d.feature_variant_id)}
                    >
                      <Table.Cell textAlign="center">
                        {d.is_control_variant && <span style={{ color: 'green' }} role="img" aria-label="check">&#10004;</span>}
                      </Table.Cell>
                      <Table.Cell>{d.name}</Table.Cell>
                      {!feature.ignore_points
                        && <Table.Cell textAlign="center">{d.points}</Table.Cell>
                      }
                      <Table.Cell textAlign="center">{remainingDisplayValue(d.remaining_assignments)}</Table.Cell>
                      <Table.Cell textAlign="center">{d.desirability || '-'}</Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
                <Table.Footer>
                  <Table.Row>
                    <Table.HeaderCell colSpan="5">
                      <AddFeatureVariant
                        featureId={featureId}
                        fetch={fetchFn}
                        ignorePoints={feature.ignore_points}
                      />
                    </Table.HeaderCell>
                  </Table.Row>
                </Table.Footer>
              </Table>
              <h2>Feature Eligibility</h2>
              <Form>
                <Form.Group widths="equal">
                  <Form.TextArea
                    fluid
                    rows={3}
                    value={'eligibility' in this.state ? eligibility : feature.feature_eligibility_rule}
                    onChange={(e, { value }) => this.setState({ eligibility: value })}
                    placeholder="The caller must match this rule set"
                  />
                </Form.Group>
                <Form.Button onClick={this.saveEligibility}>Save Eligibility</Form.Button>
              </Form>
              <h2>Version Requirements</h2>
              <Form>
                <Form.Group widths="equal">
                  <Form.Input
                    label="iOS SemVer"
                    value={iosSemver !== undefined ? iosSemver : feature?.ios_semver}
                    onChange={(e, { value }) => this.setState({ iosSemver: value })}
                    placeholder="The caller must match this semver on iOS"
                  />
                  <Form.Input
                    label="Android SemVer"
                    value={androidSemver !== undefined ? androidSemver : feature?.android_semver}
                    onChange={(e, { value }) => this.setState({ androidSemver: value })}
                    placeholder="The caller must match this semver on Android"
                  />
                  <Form.Input
                    label="Other SemVer"
                    value={otherSemver !== undefined ? otherSemver : feature?.other_semver}
                    onChange={(e, { value }) => this.setState({ otherSemver: value })}
                    placeholder="The caller must match this semver on non-iOS/Android"
                  />
                  <Form.Button label="&nbsp;" onClick={this.saveVersions}>Save Version Changes</Form.Button>
                </Form.Group>
              </Form>
              <h2>Spec</h2>
              <Form>
                <TextArea
                  ref={(t) => { this.textArea = t; }}
                  fluid
                  readOnly
                  value={JSON.stringify(data, null, '\t')}
                />
                <Button
                  style={{ marginTop: 10 }}
                  onClick={(e) => {
                    e.preventDefault();
                    this.textArea.select();
                    document.execCommand('copy');
                  }}
                >
Copy Spec to Clipboard
                </Button>
              </Form>
            </Dimmer.Dimmable>
          </div>
        </Nav>
      </Auth>
    );
  }
}

BaseFeature.propTypes = {
  fetch: PT.func.isRequired,
  data: PT.shape({
    feature: PT.shape({
      record_assignments: PT.bool,
      record_to_analytics: PT.bool,
      ignore_points: PT.bool,
    }),
    variants: PT.arrayOf(PT.shape({
      name: PT.string,
    })),
  }),
  featureId: PT.number,
  loading: PT.bool,
  onSelected: PT.func.isRequired,
};

BaseFeature.defaultProps = { loading: false, data: null, featureId: null };

export const Feature = withRouter(({ history, match }) => (
  <WithData url={`/system/features/${match.params.featureId}`}>
    <BaseFeature
      featureId={Number(match.params.featureId)}
      onSelected={id => history.push(`/system/features/variants/${id}`)}
    />
  </WithData>
));
